import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Avatar, Button, Grid, Hidden, InputLabel, ListItemIcon, ListItemText, Menu, MenuItem, Paper,
         Tab, Tabs, TextField, Typography
} from '@mui/material';

import { AttachMoney, CloudQueue, Contacts, MonetizationOn } from '@mui/icons-material';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';

import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';

import { amber, red, green } from '@mui/material/colors';

import ConfirmationDialog from '../ui/ConfirmationDialog';
import AccountRelationUpdateMutation from './AccountRelationUpdateMutation';
import VppRTUpdateMutation from './VppRTUpdateMutation';
import ResellerSubscriptions from './ResellerSubscriptions';
import VendorTransactions from './VendorTransactions';
import ResellerCommPrograms from './ResellerCommPrograms';
import VendorProgramForm from '../forms/VendorProgramForm';
import { getValueForRender } from '../../utils/InputUtils';

import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import SaveButton from '../ui/SaveButton';
import ServerError from '../ui/ServerError';

const abEnvironment = new AbEnvironment();

const ResellerVendorQuery = graphql`
query ResellerVendorQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountId
    parentId
    accountType
    accountNum
    accountName
    description
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    hubspotId
    achActive
    vppRTsByPartnerId {
      nodes {
        vpprId
        stage
        parmValue
        vppId
        notes
        repFname
        repLname
        repEmail
        vppTByVppId {
          formId
          formTByFormId {
            formResponseTsByFormId (condition: {accountId: $accountId}) {
              totalCount
            }
          }
        }
      }
    }
    accountRelationTsByRelatedId {
      nodes {
        accountRelationId
        notes
        accountId
      }
    }
    accountTByParentId {
      appTsByAccountId {
        nodes {
          appId
          appName
        }
      }
    }
    paymentSourceTsByAccountId(orderBy: PS_ORDER_ASC) {
      nodes {
        psId
        psOrder
        extSource
        extPsId
      }
    }
    customerAppTsByCustomerAccountId {
      nodes {
        customerAppId
        parentAppId
        appId
        bcId
        appStatus
        bindCardTByBcId {
          extSource
          extBcId
          bcStatus
          firstName
          lastName
          address1
          city
          state
          country
          postalCode
        }
        atId
        assetTypeTByAtId {
          atCode
          atSymbol
          placement
        }
        appSkuPrice
        appMarkupAbsolute
        appMarkupPercentage
        appStatus
        notes
        paymentOrder
        marqetaMid
        appTByAppId {
          parentAppId
          oemAppId
          appUrl
          appLogo
          appName
          appSku
          appType
        }
      }
    }
    userTsByAccountId(first: 20) {
      totalCount
      nodes {
        userId
        userName
        userRole
        lastName
        firstName
        email
        phone
        address1
        address2
        city
        state
        country
        postalCode
        marqetaUid
        userArole
      }
    }
  }
}
`;

const styleSheet = theme => ({
  tabroot: {
    flexGrow: 1,
    margin: 0,
    width: '100%',
    //backgroundColor: theme.palette.primary.light,
    borderBottom: '2px solid #dedede'
  },
  flex: {
    flex: 1,
  },
  statusButton: {
    color: '#444'
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'left',
    // color: theme.palette.text.secondary,
    maxWidth: 1200,
    marginTop: 16,
  },
  avatar: {
    marginRight: 20,
    float: 'left',
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.text.secondary
  },
  selectField: { 
    maxWidth: 120
  },
  tabScrollButton:{
    flex: '0 0 0px'
  },
  paper2: {
    textAlign: 'left',
    //color: theme.palette.text.secondary,
    marginTop: 16,
    marginBottom: 16,
    paddingTop: 16,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
  },
  /* Hidden below is sort of a hack.  Without overflow hidden - scroll bars appear but were not revealing 
  more data.  It was not obvious what is causing it.  Needs more research */

  /* Screen size specific styles */
  [theme.breakpoints.up('xs')]: {
    tabContainer: {
      padding: 0,
      overflow: 'hidden'
    },

    /* Tab navigation */
    tab: {
      fontSize: 'xx-small',
    },
    labelContainer: {
      paddingLeft: 4,
      paddingRight: 4,
    },
    label: {
      fontSize: 'xx-small',
    },
    labelWrapped: {
      fontSize: 'xx-small',
    },
  }
  ,
  [theme.breakpoints.up('sm')]: {
    tabContainer: {
      padding: 8,
      overflow: 'hidden'
    },

    /* Tab navigation */
    tab: {
      fontSize: 'x-small',
    },
    labelContainer: {
      paddingLeft: 8,
      paddingRight: 8,
    },
    label: {
      fontSize: 'x-small',
    },
    labelWrapped: {
      fontSize: 'x-small',
    },
  },
  [theme.breakpoints.up('md')]: {
    tabContainer: {
      padding: 20,
      overflow: 'hidden'
    },
  
    /* Tab navigation */
    tab: {
      fontSize: '0.75rem',
    },
    labelContainer: {
      paddingLeft: 12,
      paddingRight: 12,
    },
    label: {
      fontSize: '0.75rem',
    },
    labelWrapped: {
      fontSize: '0.75rem',
    },
  }
});

function TabContainer(props) {
  const classes = props.classes;
  return <div className={classes.tabContainer}>{props.children}</div>;
}

class ResellerVendor extends Component {

  constructor(props) {
    super(props);

    this.state = {
      value: 0,
      showSpinner: false,
      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null,
      accountId: null,
      notes: null,
      firstName: null,
      lastName: null,
      email: null,
      reFetch: 0,
      anchorEl: null,
      confirmActivate: false,
      confirmDecline: false,
      confirmSuspend: false,
      confirmReActivate: false,
      partnerEmailValid: true
    }
  }

  componentDidMount() {
    if (abEnvironment.isSegmentActive()) { window.analytics.page(); }

    // Is there something in local storage?
    let theValue = 0;
    let tabval = localStorage.getItem('customerTabVal');
    if (tabval !== null) {
      theValue = parseInt(tabval, 10);
    }

    this.setState({value: theValue});
  }

  handleChange = event => {
    this.setState({ name: event.target.value });
  };

  handleTabChange = (event, value) => {
    this.setState({ value });
    localStorage.setItem('customerTabVal', value);
  }

  setTabValue = (tv) => {
    this.setState({value: tv});
    localStorage.setItem('customerTabVal', tv);
  }

  goTo(route) {
     this.props.history.push(`/${route}`);
  }
 
  goToUserCreateNonPrimary(route, propsToCache) {
    //here it is not the primary user, just pass the company address.
    let param = {
      'address1': propsToCache.address1,
      'address2': propsToCache.address2,
      'city': propsToCache.city,
      'state': propsToCache.state,
      'country': propsToCache.country,
      'postalCode': propsToCache.postalCode

    }
    localStorage.setItem('userCreateParamProps', JSON.stringify(param));
    this.props.history.push(`/${route}`);
  }
 
  goToUserCreate(route, propsToCache) {
    localStorage.setItem('userCreateParamProps', JSON.stringify(propsToCache));
    this.props.history.push(`/${route}`);
  }

  disableAccountSave(accountRelation) {
    if (accountRelation === null)
      return true;

    let retval = this.disableAttributeCheck(this.state.notes, accountRelation.notes);
    return retval;
  }


  disableVppRSave(vppRRelation) {
    if (vppRRelation === null) {
      return true;
    }

    const { validateEmail } = this.props.auth;

    let retval = (this.disableAttributeCheck(this.state.notes, vppRRelation.notes)
    //nullable columns are different; allow empty
                  && this.disableAttributeCheckNullable(this.state.firstName, vppRRelation.repFname)
                  && this.disableAttributeCheckNullable(this.state.lastName, vppRRelation.repLname)
                  && this.disableAttributeCheckNullable(this.state.email, vppRRelation.repEmail) )
                  || (Boolean(this.state.email) && !validateEmail(this.state.email));
    return retval;
  }

  disableAttributeCheck(stateVar, propsVar)
  {
      if (stateVar !== null)
      {
          if (propsVar === null && stateVar === '')
              return true;

          if ((propsVar !== null && propsVar !== '') && stateVar === '')
              return true;

          if (propsVar !== stateVar)
              return false;
      }
      return true;
  }

  disableAttributeCheckNullable(stateVar, propsVar) {
    //initial state variable value is always null; unchanged will have null value.
      if (stateVar !== null) {

          // if they're not equal or vendor changes the value to empty, allow save.
          if (propsVar !== stateVar || stateVar === '')
              return false;
      }
      return true;
  }

  showSpinner() {
    return this.state.showSpinner;
  }

  setCountry    = (country)    => { this.setState({country:    country}); }  
  setRegion     = (region)     => { this.setState({state:      region}); }  
  setCity       = (city)       => { this.setState({city:       city}); }  
  setPostalCode = (postalCode) => { this.setState({postalCode: postalCode}); }  
  setAddress1   = (address1)   => { this.setState({address1:   address1}); }  
  setAddress2   = (address2)   => { this.setState({address2:   address2}); }  

  masqueradeAs(u_id, a_id, role, userName) {
    this.props.auth.masqueradeAs(u_id, a_id, role, userName);
  }

  handleMenuClose = () => {
    this.setState({anchorEl: null});
  }
  handleMenuClick = event => {
    this.setState({anchorEl: event.currentTarget});
  }

  render() {
    const { isAuthenticated, isAuthenticatedAppBind, isAuthenticatedVendor, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;
    const { value } = this.state;
    const okColor = green[300];
    const cancelColor = red[300];
    const statusWaiting = amber[400];

    let vendorAccountId = abEnvironment.getUserAttribute('a_id');

    // Experiences for different personas...
    if (!isAuthenticated() || needAssent()) {
      return (
        <Redirect to='/home' />
      );
    } else if (isAuthenticatedVendor() || isAuthenticatedAppBind()) {
      const params = new URLSearchParams(this.props.location.search);
      let acctId = params.get('accountId');
      let fp = params.get('fp');

      let backText = "Partners";
      

      if (fp != null && fp !== '') {
        backText = fp;
      }
      return (
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={ResellerVendorQuery}
          variables={{accountId: acctId, forceFetch: this.state.reFetch}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              if (props.accountTByAccountId === null || props.accountTByAccountId.accountType !== 3)
              {
                return (
                  <PageTitle title="Partner not found" showBackButton={true} name={backText} history={this.props.history}/>
                );
              }
  
              let customerName = getValueForRender(this.state.accountName,
                    props.accountTByAccountId.accountName,'');
              let customerInitials = customerName[0];

              let address = {
                country:    getValueForRender(this.state.country,    props.accountTByAccountId.country,    ''),
                state:      getValueForRender(this.state.state,      props.accountTByAccountId.state,      ''),
                city:       getValueForRender(this.state.city,       props.accountTByAccountId.city,       ''),
                postalCode: getValueForRender(this.state.postalCode, props.accountTByAccountId.postalCode, ''),
                address1:   getValueForRender(this.state.address1,   props.accountTByAccountId.address1,   ''),
                address2:   getValueForRender(this.state.address2,   props.accountTByAccountId.address2,   '')
              };

              //the query above may return multiple account relationships for other vendors, find the one for this particular vendor
              let theAccountRelation = null;
              for (var i = 0; i < props.accountTByAccountId.accountRelationTsByRelatedId.nodes.length; i++) {
                //console.log(vendorAccountId + '/' + props.accountTByAccountId.accountRelationTsByRelatedId.nodes[i].accountId + 'do they equal=' + (vendorAccountId === props.accountTByAccountId.accountRelationTsByRelatedId.nodes[i].accountId));
                if (vendorAccountId === props.accountTByAccountId.accountRelationTsByRelatedId.nodes[i].accountId) {
                  theAccountRelation = props.accountTByAccountId.accountRelationTsByRelatedId.nodes[i];
                }
              }

              //fields used for rendering the vendor program form per the partner account:
              //accId is already fine.
              let vppRForPartner = props.accountTByAccountId.vppRTsByPartnerId.nodes[0];
              let formId = null;
              let formAnswered = false;
              if (vppRForPartner && vppRForPartner.vppTByVppId) {
                formId = vppRForPartner.vppTByVppId.formId;
                if (formId === null) {
                  //no need to check form for answers
                } else {
                  if (vppRForPartner.vppTByVppId.formTByFormId.formResponseTsByFormId.totalCount > 0) {
                    formAnswered = true;
                  }
  
                }

              }

              //resellers should not be looking at stage 0 partners (privacy issue)
              if (vppRForPartner && vppRForPartner.stage === 0) {
                return (<Redirect to='/vendorreseller' /> );
              }

              let stage = null;
              if (vppRForPartner) {
                stage = vppRForPartner.stage;
              }

              return (
                <Grid container spacing={3} direction="row" justifyContent="flex-start">
                  <Grid item xs={12}>
                    <PageTitle title="Partner details" showBackButton={true} name={backText} noMargin={true} history={this.props.history}/>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={2} direction="row" justifyContent="space-between" style={{marginBottom: '10px'}}>
                        <Grid item>
                          <Grid container spacing={0} direction="row" justifyContent="flex-start" alignItems="center">
                            <Grid item>
                              <Avatar className={classes.avatar}>{customerInitials}</Avatar>
                            </Grid>
                            <Grid item>
                              <Typography variant="h5" className={classes.flex}>
                                {customerName}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>

                        {vppRForPartner &&
                        <Grid item>
                          <Button color="secondary" 
                            aria-owns={this.state.anchorEl ? 'simple-menu': undefined}
                            aria-haspopup='true'
                            onClick={this.handleMenuClick}
                            className={classes.statusButton}
                          >
                          
                            {vppRForPartner.stage === 1 &&
                            <React.Fragment>
                                <HourglassEmpty htmlColor={statusWaiting} /><Hidden only={['xs', 'sm']}>&nbsp;Applied&nbsp;</Hidden><ArrowDropDown />
                              </React.Fragment>
                            }
                            {vppRForPartner.stage === 2 &&
                              <React.Fragment>
                                <CheckCircle htmlColor={okColor} /><Hidden only={['xs', 'sm']}>&nbsp;Active&nbsp;</Hidden><ArrowDropDown />
                              </React.Fragment>
                            }
                            {vppRForPartner.stage === 3 &&
                              <React.Fragment>
                                <PauseCircleFilled htmlColor={cancelColor} /><Hidden only={['xs', 'sm']}>&nbsp;Suspended&nbsp;</Hidden><ArrowDropDown />
                              </React.Fragment>
                            }
                            {vppRForPartner.stage === 4 &&
                              <React.Fragment>
                                <Cancel htmlColor={cancelColor} /><Hidden only={['xs', 'sm']}>&nbsp;Declined&nbsp;</Hidden><ArrowDropDown />
                              </React.Fragment>
                            }
                            
                          </Button>
                          <Menu
                            id="action-menu"
                            anchorEl={this.state.anchorEl}
                            open={Boolean(this.state.anchorEl)}
                            onClose={this.handleMenuClose}>

                                {vppRForPartner.stage === 1 &&
                                  <span>
                                  <MenuItem onClick={() => {this.setState({anchorEl: null, confirmActivate:true}); }}>
                                    <ListItemIcon>
                                      <CheckCircle htmlColor={okColor} />
                                    </ListItemIcon>
                                    <ListItemText primary="Accept"/>
                                  </MenuItem>
                                  <MenuItem onClick={() => {this.setState({anchorEl: null, confirmDecline:true}); }}>
                                    <ListItemIcon>
                                      <Cancel htmlColor={cancelColor} />
                                    </ListItemIcon>
                                    <ListItemText primary="Decline"/>
                                  </MenuItem>
                                  </span>
                                }
                                {vppRForPartner.stage === 2 &&
                                  <MenuItem onClick={() => {this.setState({anchorEl: null, confirmSuspend:true}); }}>
                                    <ListItemIcon>
                                      <PauseCircleFilled htmlColor={cancelColor} />
                                    </ListItemIcon>
                                    <ListItemText primary="Suspend"/>
                                  </MenuItem>
                                }
                                {vppRForPartner.stage === 3 &&
                                  <MenuItem onClick={() => {this.setState({anchorEl: null, confirmReActivate:true}); }}>
                                    <ListItemIcon>
                                      <CheckCircle htmlColor={okColor} />
                                    </ListItemIcon>
                                    <ListItemText primary="Activate"/>
                                  </MenuItem>
                                }
                                {vppRForPartner.stage === 4 &&
                                  <MenuItem onClick={() => {this.setState({anchorEl: null, confirmActivate:true}); }}>
                                    <ListItemIcon>
                                      <CheckCircle htmlColor={okColor} />
                                    </ListItemIcon>
                                    <ListItemText primary="Activate"/>
                                  </MenuItem>
                                }

                            </Menu>
                        </Grid>
                        }

                      </Grid>

                      <Grid container spacing={0} direction="row" justifyContent="space-around">

                        <Grid item xs={12}>
                          <Tabs
                            value={value}
                            onChange={this.handleTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                            scrollButtons="auto"
                            classes={{root: classes.tabroot, scrollButtons: classes.tabScrollButton}}
                          >
                            <Tab label="Contact" icon={<Contacts />}
                              classes={{
                                  root: classes.tab,
                                  wrapper: classes.labelWrapped,
                                }}
                            />
                            <Tab label="Commissions" icon={<AttachMoney />}
                              classes={{
                                  root: classes.tab,
                                  wrapper: classes.labelWrapped,
                                }}
                            />
                            <Tab label="Subscriptions" icon={<CloudQueue />}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />
                            <Tab label="Transactions" icon={<MonetizationOn />}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />
                          </Tabs>
                        </Grid>
                        <Grid item xs={12} style={{marginTop: '10px'}}>
                          {value === 0 &&
                            <TabContainer {...this.props}>
                              <Paper className={classes.paper2}>
                                <Grid container spacing={2} justifyContent="flex-start">
                                  <Grid item xs={12}>
                                    <Grid container spacing={0}>
                                      <Grid item >
                                        <Typography align="left" variant="h6">Contact</Typography>
                                      </Grid>                          
                                    </Grid>
                                  </Grid>
                                  <Grid item style={{minWidth:'300px'}}>
                                    <div>
                                      <Grid container spacing={0} direction="column" justifyContent="flex-start" >
                                        <Grid item>
                                          <InputLabel shrink style={{fontSize:"16px", marginBottom:"8px"}}>Primary contact</InputLabel>
                                        </Grid>
                                        <Grid item>
                                          <Typography align="left" variant="body1" data-recording-sensitive>{props.accountTByAccountId.contactFirstName}&nbsp;{props.accountTByAccountId.contactLastName}</Typography>
                                        </Grid>
                                        <Grid item>
                                          <Typography  align="left" variant="body1" data-recording-sensitive>{props.accountTByAccountId.contactEmail}</Typography>
                                        </Grid>
                                      </Grid>
                                    </div>
                                  </Grid>

                                  {/* I want to condense these, as there are lots of repeated pieces (only non-repeated is address content) */}
                                  {address.address1 === '' &&
                                    <Grid item>
                                      <div>
                                        <Grid container spacing={0} direction="column" justifyContent="flex-start" >
                                          <Grid item>
                                            <InputLabel shrink style={{fontSize:"16px", marginBottom:"8px"}}>Billing address</InputLabel>
                                          </Grid>
                                          <Grid item>
                                            <Typography  align="left" variant="body1" data-recording-sensitive>Not specified</Typography>
                                          </Grid>
                                        </Grid>
                                      </div>
                                    </Grid>
                                  }
                                  {address.address1 !== '' &&
                                    <Grid item>
                                      <div>
                                        <Grid container spacing={0} direction="column" justifyContent="flex-start" >
                                          <Grid item>
                                            <InputLabel shrink style={{fontSize:"16px", marginBottom:"8px"}}>Billing address</InputLabel>
                                          </Grid>
                                          <Grid item>
                                            <Typography align="left" variant="body1" data-recording-sensitive>{address.address1}</Typography>
                                          </Grid>
                                          {address.address2 && address.address2 !== null && address.address2 !== 'null' &&
                                          <Grid item>
                                            <Typography align="left" variant="body1" data-recording-sensitive>{address.address2}</Typography>
                                          </Grid>
                                          }
                                          <Grid item>
                                            <Typography align="left" variant="body1" data-recording-sensitive><span>{address.city}</span>, <span>{address.state}</span>, <span>{address.country}</span> <span>{address.postalCode}</span></Typography>
                                          </Grid>
                                        </Grid>
                                      </div>
                                    </Grid>
                                  }

                                  </Grid>
                                </Paper>

                                {formId && 
                                <Paper  className={classes.paper2}>
                                  <Grid container spacing={2} justifyContent="flex-start">
                                  {formAnswered &&
                                  <Grid item xs={12}>
                                    <VendorProgramForm
                                      accountId={acctId}
                                      formId={formId}
                                      auth={this.props.auth} history={this.props.history} />
                                  </Grid>
                                  
                                  }
                                  {!formAnswered &&
                                  <React.Fragment>
                                    <Grid item xs={12}>
                                      <Grid container spacing={0}>
                                        <Grid item >
                                          <Typography align="left" variant="h6">Application</Typography>
                                        </Grid>                          
                                      </Grid>
                                    </Grid>
                                  
                                    <Grid item xs={12}>
                                      <Typography><strong>Partner has no questionnaire responses</strong></Typography>
                                    </Grid>
                                    </React.Fragment>
                                  }
                                  {vppRForPartner.stage === 1 &&
                                        <Grid item xs={12}>
                                            <Grid container spacing={2} justifyContent="flex-start">

                                              <Grid item>
                                                <Button color="primary" variant="contained" 
                                                          onClick={(e) => {this.setState({anchorEl: null, confirmActivate:true});}}>
                                                    Accept
                                                </Button>
                                              </Grid>
                                              <Grid item>
                                                <Button color="primary" variant="outlined" 
                                                          onClick={(e) => {this.setState({anchorEl: null, confirmDecline:true}); }}>
                                                    Decline
                                                </Button>
                                              </Grid>
                                          </Grid>
                                        </Grid>
                                    }
                                  </Grid>
                                </Paper>
                                }
                                <Paper className={classes.paper2}>
                                  <Grid container spacing={2} justifyContent="flex-start">
                                    {!vppRForPartner && theAccountRelation &&
                                    <React.Fragment>
                                    <Grid item xs={12}>
                                      <TextField
                                        id="notes" label="Notes"
                                        value={getValueForRender(this.state.notes, theAccountRelation.notes, '') }
                                        fullWidth
                                        multiline
                                        onChange={ (e) => { this.setState({ notes: e.target.value }); } }
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <SaveButton
                                        snackbarOpen={this.state.saveSnackbarOpen}
                                        autoHideDuration={this.state.saveSnackbarDuration}
                                        onSaveClick={() => { this._updateAccountRelation(theAccountRelation); }}
                                        onSnackbarClose={() => { this._onSnackbarClose(); }}
                                        status={this.state.saveStatus}
                                        message={this.state.saveMessage}
                                        disabled={this.disableAccountSave(theAccountRelation)}/>
                                    </Grid>
                                    </React.Fragment>
                                    }
                                    {vppRForPartner &&
                                    <React.Fragment>
                                      <Grid item xs={12}>
                                        <Grid container spacing={0}>
                                          <Grid item >
                                            <Typography align="left" variant="h6">Partner management</Typography>
                                          </Grid>                          
                                        </Grid>
                                      </Grid>

                                      <Grid item xs={12} md={6}>
                                        <TextField data-recording-ignore="mask"
                                          id="firstName" label="Partner Rep First Name"
                                          value={getValueForRender(this.state.firstName, vppRForPartner.repFname, '') }
                                          fullWidth
                                          inputProps={{ maxLength: 32 }}
                                          onChange={ (e) => { this.setState({ firstName: e.target.value }); } }
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <TextField data-recording-ignore="mask"
                                          id="lastName" label="Partner Rep Last Name"
                                          fullWidth
                                          inputProps={{ maxLength: 32 }}
                                          value={getValueForRender(this.state.lastName, vppRForPartner.repLname, '') }
                                          onChange={ (e) => { this.setState({ lastName: e.target.value }); } }
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <TextField data-recording-ignore="mask"
                                          id="email" label="Partner Rep Email"
                                          value={getValueForRender(this.state.email, vppRForPartner.repEmail, '') }
                                          fullWidth
                                          inputProps={{ maxLength: 254 }}
                                          error={!this.state.partnerEmailValid}
                                          onBlur={() => { this.validatePartnerEmail() }}
                                          helperText={this.state.partnerEmailValid ? '' : 'Email is in invalid format.'}
                                          onChange={ (e) => { this.setState({ partnerEmailValid: true, email: e.target.value }); } }
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <TextField
                                          id="notes" label="Notes"
                                          value={getValueForRender(this.state.notes, vppRForPartner.notes, '') }
                                          fullWidth
                                          multiline
                                          onChange={ (e) => { this.setState({ notes: e.target.value }); } }
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <SaveButton
                                          snackbarOpen={this.state.saveSnackbarOpen}
                                          autoHideDuration={this.state.saveSnackbarDuration}
                                          onSaveClick={() => { this._updateVppRRelation(vppRForPartner); }}
                                          onSnackbarClose={() => { this._onSnackbarClose(); }}
                                          status={this.state.saveStatus}
                                          message={this.state.saveMessage}
                                          disabled={this.disableVppRSave(vppRForPartner)}/>
                                      </Grid>
                                    </React.Fragment>
                                    }

                                  </Grid>
                                </Paper>
                            </TabContainer>
                          }
                          {value === 1 &&
                            <TabContainer {...this.props}>
                              <ResellerCommPrograms resellerAccountId={props.accountTByAccountId.accountId}
                                                    resellerName={customerName}
                                                    stage={stage}
                                                    auth={this.props.auth} history={this.props.history}/>
                            </TabContainer>
                          }
                          {value === 2 &&
                            <TabContainer {...this.props}>
                              <ResellerSubscriptions resellerAccountId={props.accountTByAccountId.accountId}
                                            auth={this.props.auth} history={this.props.history}
                                            setTabVal={this.setTabValue}/>
                            </TabContainer>
                          }
                          {value === 3 &&
                            <TabContainer {...this.props}>
                              <VendorTransactions resellerAccountId={props.accountTByAccountId.accountId}
                                        history={this.props.history}
                                      auth={this.props.auth}/>
                            </TabContainer>
                          }
                        </Grid>

                      </Grid>
                      <ConfirmationDialog
                          open={this.state.confirmActivate}
                          onClose={() => { this.setState({confirmActivate:false});}}
                          onConfirm={() => { this.activateReseller(acctId, vppRForPartner); }}
                          onCancel={() => { }}
                          title="Activate partner"
                          cancelBtn="Cancel"
                          okBtn="Ok"
                          irreversibleAction={true}
                        >
                          Accept this partner into your program. They’ll gain access to the commissions plans in this program. You can customize their commissions after you accept.
                      </ConfirmationDialog>  
                      <ConfirmationDialog
                          open={this.state.confirmReActivate}
                          onClose={() => { this.setState({confirmReActivate:false});}}
                          onConfirm={() => { this.reactivateReseller(vppRForPartner); }}
                          onCancel={() => { }}
                          title="Reactivate partner"
                          cancelBtn="Cancel"
                          okBtn="Ok"
                          irreversibleAction={true}
                        >
                          Activating this partner will make them part of the program again.  Proceed?
                      </ConfirmationDialog>  
                      <ConfirmationDialog
                          open={this.state.confirmSuspend}
                          onClose={() => { this.setState({confirmSuspend:false});}}
                          onConfirm={() => { this.suspendReseller(vppRForPartner); }}
                          onCancel={() => { }}
                          title="Suspend partner"
                          cancelBtn="Cancel"
                          okBtn="Ok"
                          irreversibleAction={true}
                        >
                          Suspending this partner will make them excluded from program.  Proceed?
                      </ConfirmationDialog>  
                      <ConfirmationDialog
                          open={this.state.confirmDecline}
                          onClose={() => { this.setState({confirmDecline:false});}}
                          onConfirm={() => { this.declineReseller(vppRForPartner); }}
                          onCancel={() => { }}
                          title="Decline partner application"
                          cancelBtn="Cancel"
                          okBtn="Ok"
                          irreversibleAction={true}
                        >
                          Are you sure that you want to decline the prospective partner?
                      </ConfirmationDialog>  
                    </Paper>
                  </Grid>
                </Grid>
              );
            }
            return <LoadingSpinner />
          }}
        />
      );
    } else {
        return (
        <h4>
          You do not have access to this page.
        </h4>
      );
    }
  }

  _onSnackbarClose() {
    this.setState( {saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null });
  }

  _updateAccountRelation(accountRelation) {
    this.setState({ saveStatus: 'saving' });
    const { notes } = this.state;

    AccountRelationUpdateMutation.commit(accountRelation.accountRelationId,  notes)
    .then(response => {
      let fetchCount = this.state.reFetch;
      fetchCount++;
      this.setState({ reFetch: fetchCount, showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Notes saved.', saveSnackbarDuration: 3000 });
    })
    .catch(error => {
      // console.log('Got error: ' + error);
      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Save notes failed.', saveSnackbarDuration: null });
    });
  }

  validatePartnerEmail() {
    //email is non truthy, don't try validating.
    if (!this.state.email) {
      return;
    }

    //validate and set state variable if the email is bad format; allow empty string to clear old contact
    const { validateEmail } = this.props.auth;

    let emailValid = this.state.email === '' || validateEmail(this.state.email);
    this.setState({
      partnerEmailValid : emailValid
    });
  }

  activateReseller = (acctId, vppRForPartner) => {

    this.setState({confirmActivate: false});
    const { callPartnerApi } = this.props.auth;
    let aPjwt = abEnvironment.getPjwt();
    let myAcctId = abEnvironment.getUserAttribute("a_id");


    //call processAccountPackage if reseller getting approved first time. 
    Promise.resolve()  
    .then(() => {

      let payload = {
        operation: "processAccountPackage2",
        params: {
          pJwt:       aPjwt,
          pAccountId: acctId,
          vAccountId: myAcctId,
          pValue:     vppRForPartner.parmValue
        }
      };

      return callPartnerApi('appbind/generic',
                    'POST',
                    payload)
    }).then(resp => {
      return resp.json();
    }).then((jresp) => {
      //set reseller to activated.      
      return VppRTUpdateMutation.commit(vppRForPartner.vpprId, 2, null)

    })
          
    .then(response => {
      let fetchCount = this.state.reFetch;
      fetchCount++;
      this.setState({ reFetch: fetchCount });
    }).catch(err => {
      //not much we can do
      let fetchCount = this.state.reFetch;
      fetchCount++;
      this.setState({ reFetch: fetchCount });
    });

 }

 reactivateReseller = (vppRForPartner) => {

    //set stage to a 2
    VppRTUpdateMutation.commit(vppRForPartner.vpprId, 2, null)
    .then(response => {
      let fetchCount = this.state.reFetch;
      fetchCount++;
      this.setState({ reFetch: fetchCount });
    })
    .catch(error => {
      //not much we can do
      let fetchCount = this.state.reFetch;
      fetchCount++;
      this.setState({ reFetch: fetchCount });
    });

 }

 declineReseller = (vppRForPartner) => {

    //set stage to a 4
    VppRTUpdateMutation.commit(vppRForPartner.vpprId, 4, null)
    .then(response => {
      let fetchCount = this.state.reFetch;
      fetchCount++;
      this.setState({ reFetch: fetchCount });
    })
    .catch(error => {
      //not much we can do
      let fetchCount = this.state.reFetch;
      fetchCount++;
      this.setState({ reFetch: fetchCount });
    });

  }

  suspendReseller = (vppRForPartner) => {

    //set stage to a 3
    VppRTUpdateMutation.commit(vppRForPartner.vpprId, 3, null)
    .then(response => {
      let fetchCount = this.state.reFetch;
      fetchCount++;
      this.setState({ reFetch: fetchCount });
    })
    .catch(error => {
      //not much we can do
      let fetchCount = this.state.reFetch;
      fetchCount++;
      this.setState({ reFetch: fetchCount });
    });

  }

  _updateVppRRelation(vppRForPartner) {
    this.setState({ saveStatus: 'saving' });

    const { notes, firstName, lastName, email } = this.state;
    //update notes
    VppRTUpdateMutation.commit(vppRForPartner.vpprId, null, notes, firstName, lastName, email)
    .then(response => {
      let fetchCount = this.state.reFetch;
      fetchCount++;
      this.setState({
        reFetch: fetchCount, showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Updates saved.', saveSnackbarDuration: 3000,
        //if the nullable columns were empty strings, change their state to "null" value to reflect mutation state.
        firstName: firstName === '' ? null : firstName, lastName: lastName === '' ? null : lastName, email: email === '' ? null : email, 
      });
    })
    .catch(error => {
      // console.log('Got error: ' + error);
      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Save failed.', saveSnackbarDuration: null });
    });
  }

}

ResellerVendor.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(ResellerVendor);